import React from 'react';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useDeleteMerchantRule from 'Hooks/query-hooks/single-merchant/useDeleteMerchantRule';
import useSingleMerchantErrorHandler from 'Hooks/query-hooks/single-merchant/useSingleMerchantErrorHandler';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { RulesTypes } from 'Common/enums';

import styles from './DeleteMerchantOverrideModal.css';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    id: string;
    revision: number;
    ruleType: RulesTypes;
};

const DeleteMerchantOverrideModal = ({ closeModal, isOpen, id, revision, ruleType }: Props) => {
    const { t } = useTranslation();
    const { mid } = useParams<{ mid: string }>();
    const { addAlert } = useAlertBanner();
    const errorHandler = useSingleMerchantErrorHandler();

    const handleTrackEvent = () => {
        let clickIdentifier = '';
        let text = '';
        if (ruleType === RulesTypes.ADVANCED) {
            clickIdentifier = 'Subjects - Remove Advanced Rule Override';
            text = 'Remove Advanced Subject-specific Rule Override';
        } else if (ruleType === RulesTypes.SIMPLE) {
            clickIdentifier = 'Subjects - Remove Override';
            text = 'Remove Override';
        }

        if (!clickIdentifier || !text) {
            return;
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };
    const onSuccess = () => {
        handleTrackEvent();
        addAlert({
            id: 'remove-rule',
            content: t('Merchant Override removed', { id }),
            status: 'success'
        });
        closeModal();
    };

    const deleteMutation = useDeleteMerchantRule({
        mid,
        ruleId: id,
        revision,
        mutationOptions: { onError: errorHandler, onSuccess }
    });
    return (
        <Modal
            isOpen={isOpen}
            closeModal={closeModal}
            containerClass={styles.modalContainer}
            status='warning'
            dataTestId='delete-merchant-override-modal'
        >
            <Modal.Title
                id='remove-merchant-override-modal-title'
                title={t('Remove this Merchant-specific Override?')}
                onClose={closeModal}
                showCloseIcon={false}
                statusIcon='warning'
            />
            <div className={styles.deleteWarningContent}>
                <div className={styles.merchantInfo}>
                    {t('Remove Merchant override warning', {
                        id
                    })}
                </div>
                {t('This cannot be undone')}.
            </div>
            <Modal.Footer
                id='remove-merchant-override-modal-footer'
                onActionText='Remove Override'
                disableOnAction={deleteMutation?.isLoading}
                onClose={closeModal}
                onAction={() => {
                    deleteMutation.mutate([id]);
                }}
            />
        </Modal>
    );
};

export default DeleteMerchantOverrideModal;
